import { useLazyQuery } from '@apollo/client';
import type { ContentSearchItem } from '@content-search/domain';
import { CONVERSATION_CONTEXT } from '@content-search/graphql/conversationContext';
import { transformErrors } from '@core/adapters/errors';
import type { DomainError } from '@core/domain';
import { track } from '@core/logging';
import { useCallback, useState } from 'react';

/**
 * Response type for the conversation context query
 */
export interface ConversationContextResponse {
  conversationContext: {
    targetSubjectId: string;
    targetTimestamp: string;
    targetFound: boolean;
    precedingMessages: ContentSearchItem[];
    followingMessages: ContentSearchItem[];
  };
}

/**
 * Hook for fetching conversation context
 * @returns Functions and state for fetching conversation context
 */
export function useConversationContext() {
  const [errors, setErrors] = useState<DomainError[] | null>(null);

  const [executeQuery, { loading }] = useLazyQuery<ConversationContextResponse>(CONVERSATION_CONTEXT, {
    fetchPolicy: 'network-only',
    onError: err => {
      const transformedErrors = transformErrors(err);

      // Check if this is a "no conversation found" error - if so, don't set it as an error state
      const hasConversationNotFoundError = transformedErrors.some(error => error.code === 'SVC_CONTENT_SEARCH_CONVERSATION_NOT_FOUND');

      if (!hasConversationNotFoundError) {
        setErrors(transformedErrors);
      } else {
        // Clear any existing errors since this is just a "no conversation found" case
        setErrors(null);
      }
    },
  });

  const resetErrors = useCallback(() => {
    setErrors(null);
  }, []);

  const fetchConversationContext = useCallback(
    async (params: {
      communityId: string;
      subjectId: string;
      topicName: string;
      metadata: Record<string, unknown>;
      messagesBefore?: number;
      messagesAfter?: number;
    }) => {
      resetErrors();

      try {
        const { communityId, subjectId, topicName, metadata, messagesBefore = 5, messagesAfter = 5 } = params;

        track('GetConversationContextInitiated', {
          subjectId,
        });

        const response = await executeQuery({
          variables: {
            input: {
              communityId,
              subjectId,
              topicName,
              metadataFilters: metadata,
              messagesBefore,
              messagesAfter,
            },
          },
        });

        track('GetConversationContextSucceeded', {
          subjectId,
        });

        return (
          response?.data?.conversationContext || {
            targetSubjectId: '',
            targetTimestamp: '',
            targetFound: false,
            precedingMessages: [],
            followingMessages: [],
          }
        );
      } catch (err) {
        const transformedErrors = transformErrors(err);

        // Check if this is a "no conversation found" error - if so, don't set it as an error state
        const hasConversationNotFoundError = transformedErrors.some(error => error.code === 'SVC_CONTENT_SEARCH_CONVERSATION_NOT_FOUND');

        if (!hasConversationNotFoundError) {
          track('GetConversationContextFailed', {
            subjectId: params.subjectId,
            errorMessage: err instanceof Error ? err.message : 'Unknown error',
          });

          setErrors(transformedErrors);
        } else {
          track('GetConversationContextFailed', {
            subjectId: params.subjectId,
            errorMessage: 'Conversation not found',
          });

          // Clear any existing errors since this is just a "no conversation found" case
          setErrors(null);
        }

        return {
          targetSubjectId: '',
          targetTimestamp: '',
          targetFound: false,
          precedingMessages: [],
          followingMessages: [],
        };
      }
    },
    [executeQuery, resetErrors, setErrors]
  );

  const fetchMoreContext = useCallback(
    async (params: {
      communityId: string;
      subjectId: string;
      topicName: string;
      metadata: Record<string, unknown>;
      messagesBefore?: number;
      messagesAfter?: number;
    }) => {
      resetErrors();

      try {
        const { communityId, subjectId, topicName, metadata, messagesBefore = 5, messagesAfter = 5 } = params;

        const response = await executeQuery({
          variables: {
            input: {
              communityId,
              subjectId,
              topicName,
              metadataFilters: metadata,
              messagesBefore,
              messagesAfter,
            },
          },
        });

        return (
          response?.data?.conversationContext || {
            targetSubjectId: '',
            targetTimestamp: '',
            targetFound: false,
            precedingMessages: [],
            followingMessages: [],
          }
        );
      } catch (err) {
        const transformedErrors = transformErrors(err);

        // Check if this is a "no conversation found" error - if so, don't set it as an error state
        const hasConversationNotFoundError = transformedErrors.some(error => error.code === 'SVC_CONTENT_SEARCH_CONVERSATION_NOT_FOUND');

        if (!hasConversationNotFoundError) {
          setErrors(transformedErrors);
        } else {
          // Clear any existing errors since this is just a "no conversation found" case
          setErrors(null);
        }

        return {
          targetSubjectId: '',
          targetTimestamp: '',
          targetFound: false,
          precedingMessages: [],
          followingMessages: [],
        };
      }
    },
    [executeQuery, resetErrors, setErrors]
  );

  return {
    fetchConversationContext,
    fetchMoreContext,
    isLoading: loading,
    errors,
    resetErrors,
  };
}
